import { Component, ViewEncapsulation, OnInit, Input } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, FormArray, FormControl, Validators } from '@angular/forms';
import { AlertService } from '@app/_services';
import { LazyLoadEvent, MessageService, MenuItem, TreeNode } from 'primeng/api';
import { MatDialogConfig, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { RateSeasonMonthlySummaryService } from './rate-season-monthly-summary.service';
import { PropertyConfigComponent } from '../../property-config/property-config.component';
import { RateSeasonSummaryService } from '../rate-season-summary/rate-season-summary.service';
import { CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-rate-season-monthly-summary',
    templateUrl: './rate-season-monthly-summary.component.html',
    styleUrls: ['./rate-season-monthly-summary.component.css'],
    standalone: false
})
export class RateSeasonMonthlySummaryComponent implements OnInit
{
  @Input() propertyId: any;
  // data
  advertisedRates: any = [];
  advertisedRateRows: any = [];
  // forms
  form1: UntypedFormGroup;
  formLoading = false;
  formAction: String;
  formLabels = [];
  formReadOnly = false;
  position = "top";
  seasonNotes: any;
  showNoteWindow = false;
  currentRateBucketDetailId: any;


  // to hold the current search
  currentSearchForm: any;
  // p-table
  first = 0;
  rows = 50;
  datasource;
  recordTotal: number;
  lazyLoadEvent: LazyLoadEvent;
  loading = true;
  multiSortMeta = [];
  scrollingColumns: any[] = [];
  frozenColumns: any[] = [];
  frozenWidth: string = '400px';
  dayMs = 1000 * 3600 * 24;

  // window
  innerWidth;
  innerHeight;
  selectedNode: any;
  // sortingState: { [key: string]: 'asc' | 'desc' } = {};
  sortingState: {
  [startDate: string]: {
    [field: string]: 'asc' | 'desc'
  }
} = {};
  sortingColDate = null;
  selectedSortField: string = 'occPct'; // Default selection
  showCalculationDialog = false;
  selectedMonthResult: any = null;
  selectedMonthLabel!: string;

  constructor(
    private rateSeasonMonthlySummaryService: RateSeasonMonthlySummaryService,
    private alertService: AlertService,
    public messageService: MessageService,
    private rateSeasonSummaryService: RateSeasonSummaryService,
    private dialog: MatDialog,
    private currencyPipe: CurrencyPipe,) { }


  ngOnInit(): void
  {
    this.setInnerWidthHeightParameters();
  }

  onSearchSubmit(searchForm: any)
  {
    // kill the old transformation
    this.advertisedRateRows = [];
    this.frozenColumns = [];

    // store the search
    this.currentSearchForm = searchForm;
    // Add month sorting if a column and field are selected
    if (this.sortingColDate != null && this.sortingColDate !== '' && this.selectedSortField != null && this.selectedSortField !== '') {
        // const sortOrder = this.sortingState[this.sortingColDate] || 'asc';
        const sortOrder = this.sortingState[this.sortingColDate]?.[this.selectedSortField] || 'asc';

        // Convert month string "YYYY-MM" to first day of month
        const [year, month] = this.sortingColDate.split('-').map(Number);
        const sortMonthDate = new Date(year, month - 1, 1);

        searchForm['sortMonthDate'] = sortMonthDate;        // backend expects LocalDate
        searchForm['sortMonthOrder'] = sortOrder;          // asc / desc
        searchForm['sortMonthField'] = this.selectedSortField; // field name like nightlyRateTTL
    }
    this.getRatesForNewSearch(searchForm);
  }

  getRatesForNewSearch(searchForm)
  {
    try
    {
      this.formLoading = true;
      this.rateSeasonMonthlySummaryService.searchRateSummaries(searchForm)
        .subscribe(
          res =>
          {
            this.advertisedRates = res.data.rateSummary;
            this.buildColumns();
            this.buildRows();
          },
          err =>
          {
            console.log('searchRateSummaries FAIL: ' + JSON.stringify(err));
            this.formLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: err });
          },
          () => { this.formLoading = false; });

    } catch (error)
    {
      this.alertService.error(error);
    }
  }

  get totalProperties(): number {
    if (!this.advertisedRateRows || this.advertisedRateRows.length === 0) return 0;
    return new Set(this.advertisedRateRows.map((r: any) => r.propertyCode)).size;
  }

  buildColumns()
  {
    // clear between queries
    this.scrollingColumns = [];
    this.frozenColumns = [];

    if (this.innerWidth < 768) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' }
      ];
    } else if (this.innerWidth < 992) {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Beds' }
      ];
    } else {
      this.frozenColumns = [
        { field: 'propertyCode', header: 'Property' },
        { field: 'rateBucketName', header: 'Bucket' },
        { field: 'bedrooms', header: 'Beds' },
        { field: 'annualRevTargetAmnt', header: 'Target' },
        { field: 'nightlyRateMonthTTL', header: 'Total Room Rate' },
        { field: 'rateBucketAdjusterPct', header: 'Rate Adj' }
      ];
    }

    // unfrozen scrollingColumns - use the first property returned
    for (let index = 0; index < this.advertisedRates[0]?.monthlyResults?.length; index++)
    {

      let startDate = this.advertisedRates[0]?.monthlyResults[index].yearMonth;

      this.scrollingColumns.push({
        field: index, startDate: startDate, header: this.advertisedRates[0]?.monthlyResults[index].yearMonthLabel
          , width: '70px'
      });
    }
  }

  buildRows()
  {
    // clear between queries
    this.advertisedRateRows = [];
    for (let e = 0; e < this.advertisedRates.length; e++)
    {
      // clear between queries
      let rateRow: any = {};

      // for each advertisedRate row in the advertisedRate list
      for (let index = 0; index < this.advertisedRates[e]?.monthlyResults?.length; index++)
      {
        rateRow[index] = this.advertisedRates[e]?.monthlyResults[index];
      }

      if (this.advertisedRates[e]) {
        rateRow.propertyCode = this.advertisedRates[e]?.propertyCode;
        rateRow.rateBucketName = this.advertisedRates[e]?.rateBucketName;
        rateRow.bedrooms = this.advertisedRates[e]?.bedrooms;
        rateRow.annualRevTargetAmnt = this.advertisedRates[e]?.annualRevTargetAmnt;
        rateRow.nightlyRateMonthTTL = this.advertisedRates[e]?.nightlyRateMonthTTL;
        rateRow.rateBucketAdjusterPct = this.advertisedRates[e]?.rateBucketAdjusterPct;
      }

      this.advertisedRateRows.push(rateRow);
    }
  }

  // toggleSort(sortingColDate: string) {

  //   this.sortingState = { [sortingColDate]: this.sortingState[sortingColDate] === 'asc' ? 'desc' : 'asc' };

  //   // Toggle between 'asc', 'desc', and reset
  //   this.sortingColDate = sortingColDate;

  //   // Prepare sorting parameters for API request
  //   const sortOrder = this.sortingState[sortingColDate];
  //   console.log(sortingColDate)
  //   console.log(sortOrder)
  //   if (sortOrder) {
  //     let searchForm = { ...this.currentSearchForm };
  //     searchForm['sortSeasongByStartDate'] = sortingColDate;
  //     searchForm['sortSeasonByOrder'] = sortOrder;
  //     searchForm['sortSeasonByField'] = this.selectedSortField;
  //     this.onSearchSubmit(searchForm)
  //   }
  // }

  // toggleSort(monthDate: string) {
  //   // Keep using the last selectedSortField
  //   this.sortingColDate = monthDate;

  //   if (!this.sortingState[monthDate]) {
  //     this.sortingState[monthDate] = 'asc';
  //   } else {
  //     this.sortingState[monthDate] =
  //       this.sortingState[monthDate] === 'asc' ? 'desc' : 'asc';
  //   }

  //   this.onSearchSubmit(this.currentSearchForm);
  // }



  onSortFieldChange(newValue: string) {
    if(this.sortingColDate != null && this.sortingColDate != '')
    {
      this.onSearchSubmit(this.currentSearchForm);
    }
}

  getOccupiedBackgroundColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'annualRevTargetAmnt' && fieldName !== 'nightlyRateMonthTTL' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.availability === 1)
      {
        return '#ddfada';
      }
      else
      {
        return 'none';
      }
    }
  }

  getSeasonColor(advertisedRate: any, fieldName: any)
  {
    if (advertisedRate && fieldName !== 'rateBucketName' && fieldName !== 'adrDevelopmentName' && fieldName !== 'bedrooms' && fieldName !== 'annualRevTargetAmnt' && fieldName !== 'nightlyRateMonthTTL' && fieldName !== 'propertyCode')
    {
      if (advertisedRate.rateSeasonEnd === advertisedRate.analyzedDate)
      {
        return '1px solid blue';
      }
      else
      {
        return 'none';
      }
    }
    else
    {
      return 'none';
    }
  }

  // when we change the rateBucketAdjusterPct
  onRateBucketAdjusterPctChange(propertyId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    let itemIndex = this.advertisedRateRows.findIndex(advertisedRate => advertisedRate[0].propertyId === propertyId);
    // Object.keys(this.advertisedRateRows[itemIndex]).forEach(e => console.log('key=' + e + ' value=' + JSON.stringify(this.advertisedRateRows[itemIndex][e])));

    Object.keys(this.advertisedRateRows[itemIndex]).forEach(e =>
    {
      if (this.advertisedRateRows[itemIndex][e] && this.advertisedRateRows[itemIndex][e].rateBucketAdjusterPct !== undefined)
      {
        this.advertisedRateRows[itemIndex][e].rateBucketAdjusterPct = newRrateBucketAdjusterPct;
      }
    });

    this.advertisedRateRows[itemIndex].rateBucketAdjusterPct = newRrateBucketAdjusterPct;
  }

  onRateBucketAdjusterPctSave(propertyId: number, newRateBucketAdjusterPct: number)
  {
    let rateSummary: any = {};
    rateSummary.propertyId = propertyId;
    rateSummary.rateBucketAdjusterPct = newRateBucketAdjusterPct;
    console.log('saving: ' + propertyId + ' with new value ' + newRateBucketAdjusterPct + ' object ' + JSON.stringify(rateSummary));
    this.updateRateSummary(rateSummary);
  }

  updateRateSummary(rateSummary: any)
  {

    this.formLoading = false;
    this.rateSeasonSummaryService.updateRateSummary(rateSummary)
      .subscribe(
        res =>
        {
          // console.log('searchRateSummaries SUCCESS: ' + JSON.stringify(res.data.rateSummary));
          // this.messageService.add({ severity: 'success', summary: 'Success', detail: res.message, life: 3000 });
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Season updated successfully. Rates are being generated in background.', life: 3000 });
        },
        err =>
        {
          console.log('updateRateSummary FAIL: ' + JSON.stringify(err));
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.message });
        },
        () => { this.formLoading = false; });
  }

  incRateBucketAdjPct(propertyId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    newRrateBucketAdjusterPct = +newRrateBucketAdjusterPct;
    this.onRateBucketAdjusterPctChange(propertyId, ((!newRrateBucketAdjusterPct || newRrateBucketAdjusterPct === 0) ? 0.00 : newRrateBucketAdjusterPct) + .01, rowData);
    // this.calcYearlyGrossRev(rowData);
  }

  decRateBucketAdjPct(propertyId: number, newRrateBucketAdjusterPct: number, rowData: any)
  {
    newRrateBucketAdjusterPct = +newRrateBucketAdjusterPct;
    this.onRateBucketAdjusterPctChange(propertyId, ((!newRrateBucketAdjusterPct || newRrateBucketAdjusterPct === 0) ? 0.00 : newRrateBucketAdjusterPct) - .01, rowData);
    // this.calcYearlyGrossRev(rowData);
  }

  sortBy(monthDate: string, field: string, event: MouseEvent) {
    event.stopPropagation(); // stop header click

    //Clear ALL previous sorts (month + field)
    this.sortingState = {};
    this.selectedSortField = field;
    this.sortingColDate = monthDate;

    // toggle asc/desc
    this.sortingState[monthDate] = {};
    
    // toggle asc / desc for THIS field
    const current = this.sortingState[monthDate][field];
    const next: 'asc' | 'desc' = current === 'asc' ? 'desc' : 'asc';

    //clear other sort icons in same month
    this.sortingState[monthDate] = {
      [field]: next
    };

    // trigger API search again with sorting
    this.onSearchSubmit(this.currentSearchForm);
}


  // https://stackoverflow.com/questions/59878134/primeng-table-filtering-and-sorting-on-each-column-doesnt-work
  // everytime you click in filter it just resorts.
  clickOnFilter(event: Event)
  {
    event.stopPropagation();
  }

  formatPercent(numPercent: string)
  {
    return parseFloat(numPercent).toFixed(2);
  }

  setInnerWidthHeightParameters()
  {
    this.innerWidth = window.innerWidth;
    this.innerHeight = window.innerHeight * 0.70;
    if (this.innerWidth < 768) { this.frozenWidth = '150px'; }
    else if (this.innerWidth < 992) { this.frozenWidth = '250px'; }
    else { this.frozenWidth = '400px'; }
  }

  onRowSelect(data) {
		try {
			const dialogConfig = new MatDialogConfig();

		// settings
		dialogConfig.disableClose = true;
		dialogConfig.autoFocus = true;
		dialogConfig.hasBackdrop = true;
		dialogConfig.maxWidth = "100vw";
		dialogConfig.width = "90vw";
		dialogConfig.height = "45vw";
		dialogConfig.maxHeight = "60vw";

		// pass data, ie rateBuicket: this.shortTermRentalId,
		dialogConfig.data = {
			propertyId: data.propertyId,
			rateBucketId: data.rateBucketId,
			propertyCode: data.propertyCode,
			title: 'Property Configuration'
		};

		// open the popup and get a reference to it called dialogRef
		const dialogRef = this.dialog.open(PropertyConfigComponent, dialogConfig);
		} catch (error) {
			console.log('onRowSelect(' + event + ')' + error);
		}
	}

  openCalculationDialog(monthResult: any, col: any) {
    if (!monthResult) {
      return;
    }
    this.selectedMonthLabel = monthResult.yearMonthLabel ?? col.header;
    this.selectedMonthResult = monthResult;
    this.showCalculationDialog = true;
  }

  closeCalculationDialog() {
    this.showCalculationDialog = false;
    this.selectedMonthResult = null;
  }

  formatExplanation(html: string): string {
    return html.replace(
      /\{\{amount:([\d.]+)\}\}/g,
      (_, value) => this.currencyPipe.transform(+value, 'USD', 'symbol', '1.0-2')!
    ).replace(
      /\{\{rate:([\d.]+)\}\}/g,
      (_, value) => this.currencyPipe.transform(+value, 'USD', 'symbol', '1.0-2')!
    );
  }


}
